import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  MessageSquare,
  AlertCircle,
  UserPlus,
  UserCheck,
  Ticket,
  Loader2,
} from 'lucide-react';
import { SERVER_EVENTS } from '@superchat/shared/events';
import { notificationRoute } from '@superchat/shared/notifications';
import { useWorkspace } from '../lib/workspace.jsx';
import { useAuth } from '../lib/auth.jsx';
import { usePopover } from '../lib/popover.js';
import { api } from '../lib/api.js';
import { shortTime } from '../lib/format.js';
import { createNotificationFeed } from '../lib/notification-feed.js';

const LIST_WIDTH = 340;
const PAGE_SIZE = 20;
/** When the list was last opened. Rows newer than this read as new. */
const SEEN_KEY = 'superchat:notificationsSeenAt';

const KIND = {
  message: { Icon: MessageSquare },
  emergency: { Icon: AlertCircle, tone: 'danger' },
  conversation: { Icon: UserPlus },
  assignment: { Icon: UserCheck },
  ticket: { Icon: Ticket },
};

function readSeenAt() {
  try {
    return Number(localStorage.getItem(SEEN_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * The bell: what you were notified about recently, newest first, with the
 * older ones a "Load more" away.
 *
 * Fetched fresh each time it opens rather than kept live, so the list is
 * always the server's record; the badge on the bell is what counts arrivals in
 * between.
 */
export function NotificationsMenu() {
  const { scopedApps, appIndex, allApps, onSocketEvent } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { open, setOpen, spot, box, trigger, list } = usePopover(LIST_WIDTH);

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fresh, setFresh] = useState(0);
  const [reloads, setReloads] = useState(0);
  // Frozen at the moment the list opens, so what was new stays highlighted
  // while it is being read.
  const [seenAt, setSeenAt] = useState(readSeenAt);

  const feed = useRef(null);
  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;

  const appIds = scopedApps.map((a) => a.id).join(',');

  const loadMore = useCallback(async () => {
    if (!feed.current) return;
    const current = feed.current;
    setLoading(true);
    setError(null);
    try {
      const page = await current.next();
      // A newer feed (reopened, or the scope changed) has replaced this one.
      if (feed.current !== current) return;
      setItems((existing) => [...existing, ...page.items]);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(err.message || 'Could not load notifications');
    } finally {
      if (feed.current === current) setLoading(false);
    }
  }, []);

  /* A fresh first page on every open, and whenever the scope moves under it. */
  useEffect(() => {
    if (!open) return;
    const ids = appIds ? appIds.split(',') : [];
    feed.current = createNotificationFeed({
      appIds: ids,
      pageSize: PAGE_SIZE,
      fetchPage: (appId, cursor) =>
        api.notifications.list(appId, { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }),
    });
    setItems([]);
    setHasMore(false);
    setFresh(0);
    loadMore();

    setSeenAt(readSeenAt());
    try {
      localStorage.setItem(SEEN_KEY, String(Date.now()));
    } catch {
      /* private window — the highlight just will not persist */
    }
  }, [open, appIds, reloads, loadMore]);

  /* Count what arrives while the list is closed, for the badge. */
  useEffect(
    () =>
      onSocketEvent((event, payload) => {
        const notable =
          (event === SERVER_EVENTS.MESSAGE_NEW &&
            payload.senderType === 'visitor' &&
            !payload.conversationBlocked) ||
          (event === SERVER_EVENTS.ASSIGNMENT_NEW &&
            payload.assigneeId === userIdRef.current &&
            payload.assignedById !== userIdRef.current);
        if (notable) setFresh((n) => n + 1);
      }),
    [onSocketEvent]
  );

  const openItem = (item) => {
    setOpen(false);
    navigate(notificationRoute(item));
  };

  return (
    <div className="alertmenu" ref={box}>
      <button
        type="button"
        ref={trigger}
        className={`icon-button notif__trigger${open ? ' is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={fresh ? `${fresh} new notification${fresh === 1 ? '' : 's'}` : 'Notifications'}
      >
        <Bell size={14} />
        {fresh > 0 && !open && (
          <span className="notif__badge">{fresh > 99 ? '99+' : fresh}</span>
        )}
      </button>

      {spot &&
        createPortal(
          <div
            className="alertmenu__list notif"
            role="dialog"
            aria-label="Recent notifications"
            ref={list}
            style={{ top: spot.top, left: spot.left, width: spot.width }}
          >
            <div className="notif__head">
              <span>Notifications</span>
              {fresh > 0 && (
                <button
                  type="button"
                  className="notif__refresh"
                  onClick={() => setReloads((n) => n + 1)}
                >
                  {fresh} new · Refresh
                </button>
              )}
            </div>

            {items.length === 0 && !loading && !error && (
              <div className="notif__empty">Nothing yet. New messages and chats show up here.</div>
            )}

            <ul className="notif__list">
              {items.map((item) => {
                const { Icon, tone } = KIND[item.kind] ?? KIND.message;
                const isNew = new Date(item.at).getTime() > seenAt;
                const app = appIndex[item.appId];
                return (
                  <li key={`${item.appId}:${item.id}`}>
                    <button
                      type="button"
                      className={`notif__item${isNew ? ' is-new' : ''}`}
                      onClick={() => openItem(item)}
                    >
                      <Icon
                        size={13}
                        className={`notif__icon${tone ? ` notif__icon--${tone}` : ''}`}
                      />
                      <span className="notif__text">
                        <span className="notif__title">
                          {item.kind === 'emergency' && (
                            <span className="flag flag--danger">ALERT</span>
                          )}
                          <span className="notif__who">{item.title}</span>
                          {allApps && app && (
                            <span className="notif__app" style={{ color: app.color }}>
                              {app.name}
                            </span>
                          )}
                        </span>
                        <span className="notif__body">{item.body}</span>
                      </span>
                      <span className="notif__time">{shortTime(item.at)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {error && <div className="notif__empty">{error}</div>}

            {loading ? (
              <div className="notif__empty">
                <Loader2 size={13} className="spin" /> Loading…
              </div>
            ) : (
              hasMore && (
                <button type="button" className="notif__more" onClick={loadMore}>
                  Load more
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default NotificationsMenu;
